import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import useBreadcrumbSetup from '../components/useBreadcrumbSetup';
import { NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
    faPlus, faTrash, faEllipsisH, faCheckCircle, faClipboardList,
    faHourglassHalf, faFileLines, faArrowsRotate, faHandPointer,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EntityTable from '../components/Listing/EntityTable';
import Select from '../components/Form/Fields/Select';
import FabGroup from '../components/FabGroup';
import ConfirmModal from '../components/Modal/ConfirmModal';
import Modal from '../components/Modal/Modal';
import { formatRelativeDate, formatFullDate } from '../utils/FormatDate';
import { useAuth } from '../context/AuthProvider';
import { API_BASE_URL } from '../api';

const API = `${API_BASE_URL}/api/v1/inventaire`;

const StatusBadge = ({ status }) => {
    const { t } = useTranslation();
    const map = {
        IN_PROGRESS: { labelKey: 'inventaire.status.in_progress', color: '#d97706', icon: faHourglassHalf },
        VALIDATED:   { labelKey: 'inventaire.status.validated',   color: '#16a34a', icon: faCheckCircle },
        DRAFT:       { labelKey: 'inventaire.status.draft', color: '#9ca3af', icon: faFileLines },
    };
    const s = map[status] || map.DRAFT;
    return (
        <FontAwesomeIcon
            icon={s.icon}
            style={{ color: s.color, fontSize: 16 }}
            data-tooltip-id="tooltip"
            data-tooltip-content={t(s.labelKey)}
        />
    );
};

const stringToColor = (str = '') => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    let color = '#';
    for (let i = 0; i < 3; i++) color += (`00${((hash >> (i * 8)) & 0xFF).toString(16)}`).slice(-2);
    return color;
};

const userInitials = (name = '') => {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
};

const UserAvatar = ({ name }) => (
    <span style={{
        width: 24, height: 24, borderRadius: 8, flexShrink: 0,
        background: stringToColor(name || ''), color: '#fff',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: 10, letterSpacing: '0.02em',
    }}>
        {userInitials(name)}
    </span>
);

const ModeBadge = ({ mode }) => {
    const { t } = useTranslation();
    return (
        <FontAwesomeIcon
            icon={mode === 'TOURNANT' ? faArrowsRotate : faHandPointer}
            style={{ color: mode === 'TOURNANT' ? '#7c3aed' : '#1d4ed8', fontSize: 16 }}
            data-tooltip-id="tooltip"
            data-tooltip-content={mode === 'TOURNANT' ? t('inventaire.mode.tournant') : t('inventaire.mode.manuel')}
        />
    );
};

export default function Inventaire() {
    const { t } = useTranslation();
    const { setTitle, setTitleIcon } = usePageTitle();
    const navigate = useNavigate();
    const { hasRight } = useAuth();
    const canCreate = hasRight('INVENTORY_CREATE');
    const canDelete = hasRight('INVENTORY_DELETE');
    const canApply = hasRight('INVENTORY_APPLY');
    const tableRef = useRef(null);
    const [creating, setCreating] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [applyTarget, setApplyTarget] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [newDescription, setNewDescription] = useState('');

    useEffect(() => {
        setTitle(`${t('Inventaire')}<span style="display:block;font-size:11px;font-weight:400;color:#9ca3af;margin-top:5px;">${t('inventaire.subtitle')}</span>`);
        document.title = t('Inventaire');
        setTitleIcon('ti-clipboard-list');
        return () => setTitleIcon(null);
    }, [setTitle, setTitleIcon, t]);

    useBreadcrumbSetup([{ label: t('Inventaire'), url: '/inventaire' }]);

    const formatDate = (d) => d ? new Date(d).toLocaleString('fr-MA') : '—';
    const progress = (inv) => inv.totalLignes > 0
        ? Math.round((inv.lignesComptees / inv.totalLignes) * 100)
        : 0;

    // Card-view design for this listing (Table's enableCardView feature) — mirrors the
    // table columns' key info (mode, statut, progression, écarts, créé par) in a compact tile.
    const renderInventaireCard = (row) => {
        const pct = progress(row);
        return (
            <NavLink to={`/inventaire/${row.id}`} className="card h-100 shadow-sm d-block text-decoration-none" style={{ color: 'inherit' }}>
                <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 8 }}>
                    <div className="d-flex align-items-center justify-content-between">
                        <span style={{ fontSize: 13.5, fontWeight: 600, color: '#374151' }}>
                            {row.title || `INVENT-${row.id}`}
                        </span>
                        <div className="d-flex align-items-center gap-2">
                            <ModeBadge mode={row.mode} />
                            <StatusBadge status={row.status} />
                        </div>
                    </div>

                    <span
                        style={{ fontSize: 11.5, color: '#9ca3af' }}
                        data-tooltip-id="tooltip"
                        data-tooltip-content={formatFullDate(row.date)}
                    >
                        {formatRelativeDate(row.date)}
                    </span>

                    <div>
                        <div style={{ fontSize: 12, color: '#374151', marginBottom: 4 }}>
                            {row.lignesComptees} / {row.totalLignes}
                        </div>
                        <div style={{ height: 6, background: '#e5e7eb', borderRadius: 99 }}>
                            <div style={{
                                height: 6, borderRadius: 99, width: `${pct}%`,
                                background: pct === 100 ? '#16a34a' : '#3b82f6',
                                transition: 'width 0.3s',
                            }} />
                        </div>
                    </div>

                    <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 'auto', paddingTop: 6 }}>
                        <span style={{
                            display: 'inline-block',
                            background: row.nbEcarts > 0 ? '#fee2e2' : '#f3f4f6',
                            color: row.nbEcarts > 0 ? '#dc2626' : '#9ca3af',
                            padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700,
                        }}>
                            {row.nbEcarts} {t('inventaire.column.ecarts')}
                        </span>
                        {row.createdByName && <UserAvatar name={row.createdByName} />}
                    </div>
                </div>
            </NavLink>
        );
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`${API}/${deleteTarget.id}`, { withCredentials: true });
            toast.success(t('inventaire.toast.deleted'));
            tableRef.current?.reload();
        } catch (err) {
            toast.error(err.response?.data?.message || t('inventaire.toast.delete_error'));
        } finally {
            setDeleteTarget(null);
        }
    };

    const confirmApply = async () => {
        const ids = applyTarget;
        setApplyTarget(null);
        const results = await Promise.allSettled(
            ids.map(id => axios.post(`${API}/${id}/validate`, {}, { withCredentials: true }))
        );
        const failed = results.filter(r => r.status === 'rejected').length;
        if (failed === 0) {
            toast.success(ids.length > 1 ? t('inventaire.toast.applied_multiple', { count: ids.length }) : t('inventaire.toast.applied_single'));
        } else {
            toast.warning(t('inventaire.toast.applied_partial', { success: ids.length - failed, failed }));
        }
        tableRef.current?.reload();
    };

    const openCreateModal = () => {
        setNewTitle('');
        setNewDescription('');
        setShowCreateModal(true);
    };

    const handleCreate = async () => {
        setCreating(true);
        try {
            const { data } = await axios.post(API, {
                mode: 'MANUEL',
                title: newTitle.trim() || null,
                notes: newDescription.trim() || null,
            }, { withCredentials: true });
            setShowCreateModal(false);
            navigate(`/inventaire/${data.id}`);
        } catch (err) {
            toast.error(err.response?.data?.message || t('inventaire.toast.create_error'));
        } finally {
            setCreating(false);
        }
    };

    const columns = [
        {
            Header: '#',
            accessor: 'id',
            sortable: true,
            className: 'text-center',
            Cell: (row) => <span style={{ fontSize: 14, fontWeight: 500, color: '#9ca3af' }}>{row.id}</span>,
        },
        {
            Header: t('Date'),
            accessor: 'date',
            Cell: (row) => (
                <NavLink to={`/inventaire/${row.id}`} className="cell-name" style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                    {row.title || `INVENT-${row.id}`}
                    <span
                        className="cell-sub"
                        data-tooltip-id="tooltip"
                        data-tooltip-content={formatFullDate(row.date)}
                    >
                        {formatRelativeDate(row.date)}
                    </span>
                </NavLink>
            ),
        },
        {
            Header: t('Mode'),
            accessor: 'mode',
            className: 'text-center',
            maxWidth: '70px',
            filterable: {
                component: Select,
                label: t('Mode'),
                options: [
                    { value: '', label: 'Tous' },
                    { value: 'MANUEL', label: t('inventaire.mode.manuel') },
                    { value: 'TOURNANT', label: t('inventaire.mode.tournant') },
                ],
            },
            Cell: (row) => <ModeBadge mode={row.mode} />,
        },
        {
            Header: t('Status'),
            accessor: 'status',
            className: 'text-center',
            maxWidth: '70px',
            filterable: {
                component: Select,
                label: t('Status'),
                options: [
                    { value: '', label: 'Tous' },
                    { value: 'DRAFT', label: t('inventaire.status.draft') },
                    { value: 'IN_PROGRESS', label: t('inventaire.status.in_progress') },
                    { value: 'VALIDATED', label: t('inventaire.status.validated') },
                ],
            },
            Cell: (row) => <StatusBadge status={row.status} />,
        },
        {
            Header: t('Progression'),
            accessor: 'lignesComptees',
            className: 'text-center',
            Cell: (row) => {
                const pct = progress(row);
                return (
                    <div style={{ minWidth: 120 }}>
                        <div style={{ fontSize: 12, color: '#374151', marginBottom: 4 }}>
                            {row.lignesComptees} / {row.totalLignes}
                        </div>
                        <div style={{ height: 6, background: '#e5e7eb', borderRadius: 99 }}>
                            <div style={{
                                height: 6, borderRadius: 99, width: `${pct}%`,
                                background: pct === 100 ? '#16a34a' : '#3b82f6',
                                transition: 'width 0.3s',
                            }} />
                        </div>
                    </div>
                );
            },
        },
        {
            Header: t('inventaire.column.ecarts'),
            accessor: 'nbEcarts',
            className: 'text-center',
            Cell: (row) => (
                <span style={{
                    display: 'inline-block',
                    background: row.nbEcarts > 0 ? '#fee2e2' : '#f3f4f6',
                    color: row.nbEcarts > 0 ? '#dc2626' : '#9ca3af',
                    padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700,
                }}>
                    {row.nbEcarts}
                </span>
            ),
        },
        {
            Header: t('inventaire.column.validated_at'),
            accessor: 'validatedAt',
            Cell: (row) => row.validatedAt ? (
                <span
                    style={{ fontSize: 12, color: '#9ca3af' }}
                    data-tooltip-id="tooltip"
                    data-tooltip-content={formatFullDate(row.validatedAt)}
                >
                    {formatRelativeDate(row.validatedAt)}
                </span>
            ) : <span style={{ fontSize: 12, color: '#9ca3af' }}>—</span>,
        },
        {
            Header: t('Created by'),
            accessor: 'createdByName',
            Cell: (row) => {
                if (!row.createdByName) return <span style={{ fontSize: 13, color: '#9ca3af' }}>—</span>;
                const parts = row.createdByName.split(' ');
                const display = parts.length >= 2 ? `${parts[0]} ${parts[1][0]}.` : parts[0];
                return (
                    <div className="d-flex align-items-center gap-2">
                        <UserAvatar name={row.createdByName} />
                        <span style={{ fontSize: 13, color: '#6b7280' }}>{display}</span>
                    </div>
                );
            },
        },
        {
            Header: '',
            accessor: 'actions',
            className: 'col-actions',
            Cell: (row) => (
                <div className="cell-dropdown">
                    <button
                        className="cell-actions-btn dropdown-toggle"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                    >
                        <FontAwesomeIcon icon={faEllipsisH} />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                        {canDelete && (
                            <li>
                                <button
                                    className="dropdown-item danger"
                                    disabled={row.status === 'VALIDATED'}
                                    onClick={() => setDeleteTarget(row)}
                                >
                                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                                    {t('Delete')}
                                </button>
                            </li>
                        )}
                    </ul>
                </div>
            ),
        },
    ];

    const buttons = canCreate
        ? [
            {
                id: 'new-inventaire',
                label: t('inventaire.action.new'),
                icon: faPlus,
                onClick: openCreateModal,
            },
        ]
        : [];

    return (
        <>
            <FabGroup buttons={buttons} />

            <Modal
                show={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                title={t('inventaire.action.new')}
                classSize="modal-dialog-centered"
                icon={faClipboardList}
                iconBg="#dbeafe"
                iconColor="#1d4ed8"
                footer={
                    <>
                        <button className="btn btn-light" onClick={() => setShowCreateModal(false)}>
                            {t('Cancel')}
                        </button>
                        <button
                            className="btn btn-primary fw-bold"
                            onClick={handleCreate}
                            disabled={creating}
                        >
                            {creating
                                ? <><span className="spinner-border spinner-border-sm me-2" />{t('inventaire.action.creating')}</>
                                : <><FontAwesomeIcon icon={faPlus} className="me-2" />{t('inventaire.action.create')}</>
                            }
                        </button>
                    </>
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                            {t('Title')} <span style={{ fontWeight: 400, color: '#9ca3af' }}>({t('optional')})</span>
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder={t('inventaire.field.title_placeholder')}
                            value={newTitle}
                            onChange={e => setNewTitle(e.target.value)}
                            autoFocus
                            maxLength={120}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                            {t('Description')} <span style={{ fontWeight: 400, color: '#9ca3af' }}>({t('optional')})</span>
                        </label>
                        <textarea
                            className="form-control"
                            placeholder={t('inventaire.field.description_placeholder')}
                            value={newDescription}
                            onChange={e => setNewDescription(e.target.value)}
                            rows={3}
                            style={{ resize: 'vertical' }}
                        />
                    </div>
                </div>
            </Modal>

            <ConfirmModal
                show={!!deleteTarget}
                title={t('inventaire.delete_title')}
                description={deleteTarget ? t('inventaire.delete_confirm', { date: formatDate(deleteTarget.date) }) : ''}
                confirmLabel={t('Delete')}
                confirmVariant="danger"
                onClose={() => setDeleteTarget(null)}
                onConfirm={confirmDelete}
            />

            <ConfirmModal
                show={!!applyTarget}
                title={t('inventaire.apply_title')}
                description={applyTarget
                    ? applyTarget.length > 1
                        ? t('inventaire.apply_confirm_multiple', { count: applyTarget.length })
                        : t('inventaire.apply_confirm_single')
                    : ''}
                confirmLabel={t('Apply')}
                confirmVariant="primary"
                onClose={() => setApplyTarget(null)}
                onConfirm={confirmApply}
            />

            <EntityTable
                ref={tableRef}
                endpoint={API}
                columns={columns}
                defaultSort="date"
                defaultDirection="desc"
                pageSize={20}
                emptyMessage={t('inventaire.empty')}
                searchable={false}
                searchPlaceholder={t('inventaire.search_placeholder')}
                enableCardView={true}
                renderCard={renderInventaireCard}
                multiSelect={true}
                selectionActions={canApply ? [
                    {
                        label: t('Apply'),
                        icon: faCheckCircle,
                        variant: 'primary',
                        onClick: (ids) => {
                            const rows = tableRef.current?.getData() ?? [];
                            const applicable = ids.filter(id => {
                                const row = rows.find(r => String(r.id) === String(id));
                                return !row || row.status !== 'VALIDATED';
                            });
                            if (!applicable.length) {
                                toast.info(t('inventaire.toast.already_validated'));
                                return;
                            }
                            setApplyTarget(applicable);
                        },
                    },
                ] : []}
            />
        </>
    );
}
